/**
 * AuditPage.tsx — §14, the record of who changed what.
 *
 * Human and agent activity appear in one list, distinguished by `actorType`
 * rather than separated into different views: the point of §7.5 keeping the
 * identities distinct is that a reviewer can see both in the same timeline and
 * tell them apart.
 */

import { useClient } from "../../api/ClientContext";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";

export function AuditPage({ projectId }: { projectId: string }): JSX.Element {
  const client = useClient();
  const audit = useAsync(() => client.audit(projectId, 200), [client, projectId]);

  return (
    <div className="page">
      <header className="page-head"><h1>Audit</h1><p className="muted">{projectId}</p></header>
      <ErrorBanner error={audit.error} onDismiss={audit.reload} />
      <Panel title="Recent activity">
        {audit.loading ? <Empty>Loading…</Empty>
          : !audit.data?.length ? <Empty>Nothing recorded yet.</Empty>
          : (
            <table className="audit">
              <thead>
                <tr><th>When</th><th>Actor</th><th>Via</th><th>Operation</th><th>Result</th><th>Revision</th></tr>
              </thead>
              <tbody>
                {audit.data.map(event => (
                  <tr key={event.eventId} className={`result-${event.result}`}>
                    <td>{event.timestamp.slice(0, 19).replace("T", " ")}</td>
                    <td>
                      {event.actorDisplayName ?? event.actorId}
                      <span className="muted"> ({event.actorType})</span>
                    </td>
                    <td>{event.sourceClient}</td>
                    <td><code>{event.operation}</code></td>
                    <td>{event.result}</td>
                    <td><code>{event.resultingRevision?.slice(0, 12) ?? ""}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </Panel>
    </div>
  );
}
